/* eslint-disable max-lines -- not split yet: cargo-expectation lookups, step selection and scan interpretation are separable */
import {
  type CargoOperation,
  type DateTimeString,
  RouteCargoExpectationNotBroughtReason,
  RouteCargoExpectationNotBroughtReasonLabels,
  RouteCargoExpectationOverrideReason,
  RouteCargoExpectationOverrideReasonLabels,
  RouteCargoExpectationVoidReason,
  type RouteItemCargoExpectationRecord,
  type RouteItemCargoExpectationScanBulkSummaryItem,
  type TireSeason
} from '@navio/nsdk';
import {
  CAR_WHEEL_ALL_SEASON_VALUE,
  CAR_WHEEL_SUMMER_VALUE,
  CAR_WHEEL_WINTER_VALUE,
  type CarWheelSeasonValue
} from 'noddi-types';
import { type CarProps, getCarDisplayName, getCarLicensePlateText } from '../car/car';
import { getLabelMapFromEntries } from '../options/choiceOptions';
import { pluralizeWithValue } from '../string/string';
import { formatTireDimensionFromParts } from '../tire/tire';
import { type CargoDescription, describeCargo, isTireCargoExpectation } from './cargo';

export type ReloadStopLabel = 'Load' | 'Reload' | 'Unload';

/**
 * Both dropoff + pickup wheel work → "Reload".
 * Only dropoff (at reload) → "Unload".
 * Only pickup (from reload) → "Load".
 * Neither → "Reload".
 */
export function getReloadStopLabel(
  reloadHasDropoffUnits: boolean | null | undefined,
  reloadHasPickupUnits: boolean | null | undefined
): ReloadStopLabel {
  const hasDropoff = reloadHasDropoffUnits === true;
  const hasPickup = reloadHasPickupUnits === true;
  if (hasDropoff && hasPickup) {
    return 'Reload';
  }
  if (hasDropoff) {
    return 'Unload';
  }
  if (hasPickup) {
    return 'Load';
  }
  return 'Reload';
}

/** Match a scanned wheel-set identifier to a non-voided expectation row (case-insensitive). */
function findActiveCargoExpectationByWheelIdentifier(
  expectations: RouteItemCargoExpectationRecord[] | undefined,
  identifier: string
): RouteItemCargoExpectationRecord | undefined {
  const list = expectations ?? [];
  const needle = identifier.trim().toUpperCase();
  if (needle === '') {
    return undefined;
  }
  return list.find((e) => e.voidedAt == null && e.storageObjectGroup.identifier.trim().toUpperCase() === needle);
}

/** How the worker app names a reload stop, in a driver's words; partner web keeps {@link ReloadStopLabel}. */
export const reloadStopNames: Record<ReloadStopLabel, string> = {
  Load: 'Pick up from storage',
  Unload: 'Deliver to storage',
  Reload: 'Deliver and pick up'
};

// --- Cargo expectations (worker reload manifest; `cargoExpectations` on `getRouteItemForReload`) ---

/**
 * User-facing labels for reload cargo rows (partner web, worker mobile).
 * API / model uses `overridden` and `not_brought`; keep this copy in sync across apps.
 */
/** Shared override-reason choices for partner web + worker deviation flows. */
export const CARGO_EXPECTATION_OVERRIDE_REASON_OPTIONS: {
  value: RouteCargoExpectationOverrideReason;
  label: string;
}[] = [
  {
    value: RouteCargoExpectationOverrideReason.LABEL_DAMAGED,
    label: RouteCargoExpectationOverrideReasonLabels.LABEL_DAMAGED
  },
  {
    value: RouteCargoExpectationOverrideReason.OTHER,
    label: RouteCargoExpectationOverrideReasonLabels.OTHER
  }
];

// The generated labels are keyed by member name, the record carries the value.
export const CARGO_EXPECTATION_NOT_BROUGHT_REASON_LABELS = getLabelMapFromEntries(
  RouteCargoExpectationNotBroughtReason,
  RouteCargoExpectationNotBroughtReasonLabels
);

/** Missing-reason choices; the facility's own report is a choice only for a set the facility reported. */
export function getNotBroughtReasonOptions(isReportedByFacility: boolean): RouteCargoExpectationNotBroughtReason[] {
  return [
    ...(isReportedByFacility ? [RouteCargoExpectationNotBroughtReason.EXTERNAL_CHECK_ISSUE] : []),
    RouteCargoExpectationNotBroughtReason.NOT_FOUND,
    RouteCargoExpectationNotBroughtReason.DAMAGED,
    RouteCargoExpectationNotBroughtReason.OTHER
  ];
}

/** Every piece of a reload step's copy, so each step reads the same everywhere it is named. */
const RELOAD_STEP_COPY: Record<
  ReloadStep,
  {
    title: string;
    shortTitle: string;
    description: (cargo: string) => string;
    done: string;
    progress: string;
    scanCta: string;
    /** Starts the stop when the worker is already there; names its first step. */
    startCta: string;
    verb: string;
    where: string;
    reason: string;
  }
> = {
  1: {
    title: 'Unload from vehicle',
    shortTitle: 'Unload',
    description: (cargo) => `Scan the ${cargo} to unload from the vehicle to storage`,
    done: 'Unloading done',
    progress: 'Unloaded',
    scanCta: 'Scan labels',
    startCta: 'Start unloading',
    verb: 'unload',
    where: ' from the vehicle',
    reason: 'The wheel sets you collected on your route'
  },
  2: {
    title: 'Load to vehicle',
    shortTitle: 'Load',
    description: (cargo) => `Scan the ${cargo} to load from storage onto the vehicle`,
    done: 'Loading done',
    progress: 'Loaded',
    scanCta: 'Scan labels',
    startCta: 'Start loading',
    verb: 'load',
    where: ' onto the vehicle',
    reason: 'For bookings later on your route'
  },
  3: {
    title: 'Scan printed labels',
    shortTitle: 'Labels',
    description: () =>
      'Scan the printed labels and bring them with you. You’ll attach these labels to the wheels when you pick them up on your route.',
    done: 'Printed labels done',
    progress: 'Scanned',
    scanCta: 'Scan printed labels',
    startCta: 'Start scanning labels',
    verb: 'scan',
    where: '',
    reason: 'For wheels you pick up later on your route'
  }
};

/** A step's cargo: printed labels on the labels step, otherwise wheel sets and tires. */
function describeStepCargo(step: ReloadStep, rows: RouteItemCargoExpectationRecord[]): CargoDescription {
  return step === 3
    ? { counted: pluralizeWithValue({ value: rows.length, word: 'printed label' }), kind: 'printed labels' }
    : describeCargo(rows);
}

export const reloadLabels = {
  actionChangeTireSeason: 'Change tire season',
  actionRevertToPending: 'Undo',
  revertConfirmTitle: 'Undo?',
  revertConfirmBody:
    'This clears the scan, manual completion or missing report and returns the wheel set to pending so it can be scanned.',
  /** Worker app: names the state being undone. */
  revertConfirmRowBody: (state: string) => `Undo “${state}” and return the wheel set to pending?`,
  /** Progress legend count across steps, e.g. "Without scan 3" */
  legendConfirmedWithoutScanCount: 'Without scan',
  /** Progress legend count across steps, e.g. "Missing 2" */
  legendReportedMissingCount: 'Missing',
  // Section headings: use getReloadSectionHeadings(...)
  sectionVoided: (count: number) => `Canceled or voided · ${pluralizeWithValue({ value: count, word: 'wheel set' })}`,
  sectionScannedSuffix: (done: number, total: number) => `${done} of ${total} scanned`,
  /** Scan-tab step header when counting queued unload identifiers as scanned progress. */
  /** Scan-tab header before bulk place: includes staged QR reads not yet committed to server. */
  scanStepHeaderScannedSuffix: (scanned: number, total: number) => `${scanned} of ${total} scanned`,
  sectionAllSet: (total: number) => `${total} of ${total} scanned · all done`,
  scanCardGotItPrefix: 'Got it',
  /** Shown after car line on successful scan result card (unload step). */
  scanResultReloadOperationUnload: 'Unload',
  scanResultReloadOperationLoadWheels: 'Load wheels',
  scanResultReloadOperationPrintedLabels: 'Printed labels',
  /** Scan matched a line for a different checklist step than the active tab. */
  scanCardWrongStepTitle: 'Wrong step',
  scanCardWrongStepBody:
    'This label belongs to another part of the reload checklist. Switch step or scan a line for this step.',
  scanCardAlreadyScannedTitle: 'Already scanned',
  scanCardAlreadyScannedTitleWithName: (name: string) => `Already scanned — ${name}`,
  scanCardAlreadyScannedAt: (time: string) => `These wheels were already scanned at ${time}.`,
  scanCardAlreadyScannedDetailAt: (carLine: string, time: string) => `${carLine} · scanned at ${time}`,
  scanCardAlreadyScannedShort: 'These wheels are already scanned on this reload',
  scanCardNotOnListTitle: 'Not included in this reload',
  scanCardNotOnListBody: 'These wheels are not part of this reload.',
  // Phased reload manifest (unload + load steps)
  stepTitle: (step: ReloadStep) => RELOAD_STEP_COPY[step].title,
  /** Fits one segment of the step picker. */
  stepShortTitle: (step: ReloadStep) => RELOAD_STEP_COPY[step].shortTitle,
  stepDescription: (step: ReloadStep, rows: RouteItemCargoExpectationRecord[]) =>
    RELOAD_STEP_COPY[step].description(describeStepCargo(step, rows).kind),
  stepDoneTitle: (step: ReloadStep) => RELOAD_STEP_COPY[step].done,
  nextStepCta: (step: ReloadStep, rows: RouteItemCargoExpectationRecord[]) =>
    `Next: ${RELOAD_STEP_COPY[step].verb} ${describeStepCargo(step, rows).kind}`,
  stepReason: (step: ReloadStep) => RELOAD_STEP_COPY[step].reason,
  scanLabelsCta: 'Scan labels',
  stopOfCount: (number: number, count: number) => `Stop ${number} of ${count}`,
  newLabelOnPrintListTitle: 'A new label goes on the print list',
  newLabelPrintsAt: (facilityName: string) => `It prints at ${facilityName}.`,
  stepScanCta: (step: ReloadStep) => RELOAD_STEP_COPY[step].scanCta,
  startStepCta: (step: ReloadStep) => RELOAD_STEP_COPY[step].startCta,
  completeStopCta: 'Complete stop',
  stepPreview: (step: ReloadStep, rows: RouteItemCargoExpectationRecord[]) => {
    const { verb, where } = RELOAD_STEP_COPY[step];
    return `${verb.charAt(0).toUpperCase()}${verb.slice(1)} ${describeStepCargo(step, rows).counted}${where}`;
  },
  /** Pre-start / completed manifest: section titles by transport direction (inbound → unload, outbound → load). */
  manifestOverviewWheelSetsUnloadFromVehicle: 'Wheels to unload from vehicle',
  manifestOverviewLoadOntoVehicle: (rows: RouteItemCargoExpectationRecord[]) => {
    const { kind } = describeStepCargo(2, rows);
    return `${kind.charAt(0).toUpperCase()}${kind.slice(1)} to load onto vehicle`;
  },
  manifestOverviewPrintedLabelsForVehicle: 'Printed labels to bring in the vehicle to put on picked-up wheels',
  /** No active cargo expectations on reload (empty or all voided/cancelled). */
  manifestNoCargoLinesYet: 'Nothing to load or unload. You can complete this stop.',
  /** Manifest panel header title (scan screen). */
  stepProgressTitle: (step: ReloadStep, done: number, total: number) =>
    `${RELOAD_STEP_COPY[step].progress} ${done} of ${total}`,
  deviationTypeChangeTireSeason: 'Change tire season',
  deviationTypeChangeTireSeasonSub: 'Use this if the assumed tire season is wrong for any of the wheels.',
  bulkChangeTireSeasonTitle: 'Choose tire season',
  /** The season option when the wheel set's season is known; offers the other season. */
  deviationTypeSwitchSeason: 'Wrong tire season',
  deviationTypeSwitchSeasonSub: (from: string, to: string) => `Marked as ${from}. Tap if these are ${to} wheels.`,
  changeTireSeasonTo: (to: string) => `Change to ${to}`,
  /** Opens the storage unit picker for the scanned batch; reload and transfer share it. */
  chooseStorageUnitCta: 'Choose unit',
  unloadBatchClearBatchCta: 'Clear batch',
  unloadBatchClearBatchConfirmTitle: 'Clear staged scans?',
  unloadBatchClearBatchConfirmBody:
    'Scanned wheel sets are not saved until you place them in a unit. This removes them from your batch.',
  unloadBatchSheetTitle: 'Choose storage unit',
  unloadBatchPlaceSubmitCta: 'Place here',
  unloadBatchNavigateAwayTitle: 'Leave reload?',
  unloadBatchNavigateAwayBody:
    'You have wheels staged for unload that are not placed in storage yet. Leaving will discard the batch.',
  unloadBatchNavigateAwayDiscard: 'Discard and leave',
  unloadBatchRowQueuedHint: 'Scanned · not stored yet',
  unloadBatchGotItAddedToBatch: (n: number) => `${n} in batch — place in storage when ready`,
  addToBatchCta: 'Add to batch',
  unloadBatchSwitchStepBlockedTitle: 'Place or clear batch',
  unloadBatchSwitchStepBlockedBody:
    'You still have wheels staged for unload. Place them in storage or clear the batch before loading.',
  /** Worker app: reason step of a row saved without scan. */
  whyCouldntScanTitle: 'Why couldn’t you scan it?',
  /** Worker app: header of a pending row's other deviations, under the where-are-the-wheels question. */
  somethingElseWrongTitle: 'Something else wrong?',
  /** Note field label parts (uppercase label + lowercase suffix). */
  noteLabel: 'Note',
  noteOptionalSuffix: 'optional',
  noteRequiredSuffix: 'required',
  cantScanNotePlaceholder: 'Add any detail for the office…',
  voidedRowSubtitleCancelledVoided: 'Canceled or voided',
  voidedRowSubtitleCancelled: 'Canceled',
  actionShow: 'Show',
  actionHide: 'Hide',
  genericCancel: 'Cancel',
  actionOk: 'OK',
  manifestRowFeedbackScanned: 'Scanned',
  cameraContinue: 'Continue',
  toastPlacedInStorage: 'Placed in storage',
  reportDeviationsBack: 'Back',
  reportDeviationsReasonCategory: 'Reason',
  /** Report-issue sheet: step after reason/note for inbound “mark as done”. */
  reportDeviationsInboundStorageIntro: 'Choose the storage unit where you put the wheel set.',
  reportDeviationsNotePlaceholder: 'Describe what happened…',
  reasonPickerSearchPlaceholder: 'Search reasons…',
  reasonPickerNoMatch: 'No matching reasons.',
  reasonPickerDone: 'Done',
  /** Row status badges (manifest / deviations list). */
  rowStatusBadgeScanned: 'Scanned',
  rowStatusBadgePending: 'Pending',
  rowStatusBadgeCancelled: 'Canceled'
} as const;

/** Shared toast copy for reload/transfer “already scanned” barcode results. */
export const buildAlreadyScannedToastContent = ({
  customerName,
  carLine,
  scannedAtFormatted,
  labels
}: {
  customerName: string;
  carLine: string;
  scannedAtFormatted: string | null;
  labels: {
    scanCardAlreadyScannedTitle: string;
    scanCardAlreadyScannedTitleWithName: (name: string) => string;
    scanCardAlreadyScannedAt: (time: string) => string;
    scanCardAlreadyScannedDetailAt: (carLine: string, time: string) => string;
    scanCardAlreadyScannedShort: string;
  };
}): { text1: string; text2: string } => ({
  text1:
    customerName.length > 0
      ? labels.scanCardAlreadyScannedTitleWithName(customerName)
      : labels.scanCardAlreadyScannedTitle,
  text2:
    carLine.length > 0 && scannedAtFormatted != null
      ? labels.scanCardAlreadyScannedDetailAt(carLine, scannedAtFormatted)
      : carLine.length > 0
        ? carLine
        : scannedAtFormatted != null
          ? labels.scanCardAlreadyScannedAt(scannedAtFormatted)
          : labels.scanCardAlreadyScannedShort
});

/** Build the request body for `postOverrideRouteItemCargoExpectationsBulk`. */
export function serializeOverrideReasonForApi(args: {
  overrideReason: RouteCargoExpectationOverrideReason;
  note: string;
}): { overrideReason: RouteCargoExpectationOverrideReason; overrideNote: string | null } {
  const trimmedNote = args.note.trim();
  return {
    overrideReason: args.overrideReason,
    overrideNote: trimmedNote === '' ? null : trimmedNote
  };
}

/** Build the request body for `postNotBroughtRouteItemCargoExpectationsBulk`. */
export function serializeNotBroughtReasonForApi(args: {
  notBroughtReason: RouteCargoExpectationNotBroughtReason;
  note: string;
}): { notBroughtReason: RouteCargoExpectationNotBroughtReason; notBroughtNote: string | null } {
  const trimmedNote = args.note.trim();
  return {
    notBroughtReason: args.notBroughtReason,
    notBroughtNote: trimmedNote === '' ? null : trimmedNote
  };
}

/** Reload stop lifecycle for UI (`upcoming` | `in_progress` | `completed`) from reload timestamps. */
export type ReloadLifecycleUiStatus = 'upcoming' | 'in_progress' | 'completed';

export function getReloadLifecycleUiStatusFromTimestamps(params: {
  startedAt?: DateTimeString | null;
  completedAt?: DateTimeString | null;
}): ReloadLifecycleUiStatus {
  if (params.completedAt) {
    return 'completed';
  }
  if (params.startedAt) {
    return 'in_progress';
  }
  return 'upcoming';
}

/** Non-voided rows: `done` / `total` reflect server status; optional `queuedCount` adds staged unload identifiers for display-only progress. */
export function getReloadProgress(
  expectations: RouteItemCargoExpectationRecord[] | undefined,
  options?: { queuedCount?: number }
) {
  const list = expectations ?? [];
  const active = list.filter((e) => e.voidedAt == null);
  const total = active.length;
  const pending = active.filter((e) => e.status === 'pending').length;
  const done = total - pending;
  const queued = Math.max(0, options?.queuedCount ?? 0);
  const scannedForDisplay = Math.min(total, done + queued);
  const isFullyScannedDisplay = total > 0 && scannedForDisplay >= total;
  return {
    total,
    done,
    pending,
    scannedForDisplay,
    isFullyScannedDisplay,
    queuedInBatch: queued,
    scanned: active.filter((e) => e.status === 'scanned').length,
    overridden: active.filter((e) => e.status === 'overridden').length,
    notBrought: active.filter((e) => e.status === 'not_brought').length,
    voided: list.filter((e) => e.voidedAt != null).length
  };
}

/**
 * Per-phase counts + voided buckets shown in the manifest progress legend.
 * `load` combines wheel load and printed-label lines (`cargoOperation`).
 * Voided rows are split into "Cancelled" (BOOKING_CANCELLED + MANUAL_OPS) and
 * "Couldn't complete" (BOOKING_ITEM_UNABLE_TO_COMPLETE) per design.
 */
export function getReloadPhaseCounts(expectations: RouteItemCargoExpectationRecord[] | undefined) {
  const list = expectations ?? [];
  const active = list.filter((e) => e.voidedAt == null);
  const voided = list.filter((e) => e.voidedAt != null);

  const unloadActive = active.filter((e) => e.cargoOperation === 'wheels_from_customer_to_storage');
  const loadActive = active.filter(
    (e) =>
      e.cargoOperation === 'wheels_from_storage_to_customer' || e.cargoOperation === 'labels_from_storage_to_customer'
  );
  const unloadDone = unloadActive.filter((e) => e.status !== 'pending').length;
  const loadDone = loadActive.filter((e) => e.status !== 'pending').length;

  const cancelled = voided.filter(
    (e) =>
      e.voidedReason === RouteCargoExpectationVoidReason.BOOKING_CANCELLED ||
      e.voidedReason === RouteCargoExpectationVoidReason.MANUAL_OPS
  ).length;
  const couldntComplete = voided.filter(
    (e) => e.voidedReason === RouteCargoExpectationVoidReason.BOOKING_ITEM_UNABLE_TO_COMPLETE
  ).length;

  return {
    unload: { done: unloadDone, total: unloadActive.length },
    load: { done: loadDone, total: loadActive.length },
    confirmedWithoutScan: active.filter((e) => e.status === 'overridden').length,
    reportedMissing: active.filter((e) => e.status === 'not_brought').length,
    cancelled,
    couldntComplete
  };
}

/**
 * Manifest is ready to complete when no active row is pending.
 * Empty and all-voided manifests are completable (nothing left to scan).
 */
export function getReloadCompletionState(expectations: RouteItemCargoExpectationRecord[] | undefined) {
  const list = expectations ?? [];
  const active = list.filter((e) => e.voidedAt == null);
  const pendingCount = active.filter((e) => e.status === 'pending').length;
  return {
    pendingCount,
    isReadyToComplete: pendingCount === 0
  };
}

/** Pending rows first (still need scan / confirmation / missing flow), then alphabetical by user group name. */
function compareCargoExpectationsForManifestOrder(
  a: RouteItemCargoExpectationRecord,
  b: RouteItemCargoExpectationRecord
): number {
  const pendingRank = (row: RouteItemCargoExpectationRecord) => (row.status === 'pending' ? 0 : 1);
  const rankDiff = pendingRank(a) - pendingRank(b);
  if (rankDiff !== 0) {
    return rankDiff;
  }
  const aName = a.bookingItem?.userGroup?.name ?? '';
  const bName = b.bookingItem?.userGroup?.name ?? '';
  return aName.localeCompare(bName, undefined, { sensitivity: 'base' });
}

export function groupCargoExpectationsByReloadOperation(expectations: RouteItemCargoExpectationRecord[] | undefined): {
  unload: RouteItemCargoExpectationRecord[];
  loadWheels: RouteItemCargoExpectationRecord[];
  loadLabels: RouteItemCargoExpectationRecord[];
  voided: RouteItemCargoExpectationRecord[];
} {
  const list = expectations ?? [];
  const voided = list.filter((e) => e.voidedAt != null).sort((a, b) => a.id - b.id);
  const active = list.filter((e) => e.voidedAt == null);
  return {
    unload: active
      .filter((e) => e.cargoOperation === 'wheels_from_customer_to_storage')
      .sort(compareCargoExpectationsForManifestOrder),
    loadWheels: active
      .filter((e) => e.cargoOperation === 'wheels_from_storage_to_customer')
      .sort(compareCargoExpectationsForManifestOrder),
    loadLabels: active
      .filter((e) => e.cargoOperation === 'labels_from_storage_to_customer')
      .sort(compareCargoExpectationsForManifestOrder),
    voided
  };
}

export type ReloadExpectationsGrouped = ReturnType<typeof groupCargoExpectationsByReloadOperation>;

/** Reload sub-step: unload (step 1), load wheels (step 2), or printed labels (step 3). */
export type ReloadStep = 1 | 2 | 3;

/** Steps that have any cargo rows (determines tab count / step numbering). */
function getPresentReloadSteps(expectations: RouteItemCargoExpectationRecord[] | undefined): ReloadStep[] {
  const { unload, loadWheels, loadLabels } = groupCargoExpectationsByReloadOperation(expectations);
  const steps: ReloadStep[] = [];
  if (unload.length > 0) {
    steps.push(1);
  }
  if (loadWheels.length > 0) {
    steps.push(2);
  }
  if (loadLabels.length > 0) {
    steps.push(3);
  }
  return steps;
}

/** Which {@link CargoOperation} values belong to the given manifest step. */
export function getReloadOperationsForStep(step: ReloadStep): CargoOperation[] {
  switch (step) {
    case 1:
      return ['wheels_from_customer_to_storage'];
    case 2:
      return ['wheels_from_storage_to_customer'];
    case 3:
      return ['labels_from_storage_to_customer'];
  }
}

/** Rows for the named step (non-voided, already grouped by {@link groupCargoExpectationsByReloadOperation}). */
export function getActiveRowsForReloadStep(
  grouped: ReloadExpectationsGrouped,
  step: ReloadStep
): RouteItemCargoExpectationRecord[] {
  switch (step) {
    case 1:
      return grouped.unload;
    case 2:
      return grouped.loadWheels;
    case 3:
      return grouped.loadLabels;
  }
}

/**
 * Auto-derived active step: finish unload before load wheels before labels.
 * Returns `'done'` when every active row is non-pending in all present steps.
 */
function getReloadActiveStepAuto(expectations: RouteItemCargoExpectationRecord[] | undefined): ReloadStep | 'done' {
  const { unload, loadWheels, loadLabels } = groupCargoExpectationsByReloadOperation(expectations);

  if (unload.some((e) => e.status === 'pending')) {
    return 1;
  }
  if (loadWheels.some((e) => e.status === 'pending')) {
    return 2;
  }
  if (loadLabels.some((e) => e.status === 'pending')) {
    return 3;
  }
  if (unload.length > 0 || loadWheels.length > 0 || loadLabels.length > 0) {
    return 'done';
  }
  return 'done';
}

/** Logical step when only one phase exists: use the phase that has rows. */
function getSinglePhaseLogicalStep(grouped: ReloadExpectationsGrouped): ReloadStep | null {
  const hasUnload = grouped.unload.length > 0;
  const hasLoadWheels = grouped.loadWheels.length > 0;
  const hasLoadLabels = grouped.loadLabels.length > 0;

  if (hasUnload && !hasLoadWheels && !hasLoadLabels) {
    return 1;
  }
  if (!hasUnload && hasLoadWheels && !hasLoadLabels) {
    return 2;
  }
  if (!hasUnload && !hasLoadWheels && hasLoadLabels) {
    return 3;
  }
  return null;
}

// The booking item's car when it names one, else the stored wheel set's or tires' car.
export function getCargoExpectationCar(row: RouteItemCargoExpectationRecord): CarProps | null {
  const bookingCar = row.bookingItem?.car;
  if (bookingCar && getCarDisplayName({ car: bookingCar, showYear: false })) {
    return bookingCar;
  }
  const wheelSet = row.storageObjectGroup.carWheelSet;
  if (wheelSet) {
    return { make: wheelSet.make, model: wheelSet.model, licensePlateNumber: wheelSet.licensePlate ?? undefined };
  }
  const tire = row.storageObjectGroup.carTireStorageObjects?.[0];
  if (tire?.carId != null) {
    return { make: tire.carMake, model: tire.carModel, licensePlate: tire.carLicensePlate };
  }
  return null;
}

type CargoExpectationTires = { season: TireSeason | null; summary: string };

/** Tires stored without rims (e.g. purchased tires for mounting), shown instead of a wheel set's season; null for a wheel set. */
export function getCargoExpectationTires(row: RouteItemCargoExpectationRecord): CargoExpectationTires | null {
  if (!isTireCargoExpectation(row)) {
    return null;
  }
  const tires = row.storageObjectGroup.carTireStorageObjects ?? [];
  const tire = tires[0];
  if (!tire) {
    return { season: null, summary: 'Tires' };
  }
  const { tireDimensionWidthMm: width, tireDimensionProfile: profile, tireDimensionRimInch: rim } = tire;
  return {
    season: tire.tireSeason ?? null,
    summary: [
      `${tire.tireBrandName} ${tire.tireModelName}`.trim(),
      width != null && profile != null && rim != null ? formatTireDimensionFromParts(width, profile, rim) : null,
      pluralizeWithValue({ value: tires.length, word: 'tire' })
    ]
      .filter(Boolean)
      .join(' · ')
  };
}

export function getCargoExpectationFallbackName(row: RouteItemCargoExpectationRecord): string {
  return `Booking item #${row.bookingItem?.id ?? 'unknown'}`;
}

export function getCargoExpectationCarLine(row: RouteItemCargoExpectationRecord): string {
  const car = getCargoExpectationCar(row);
  const name = car ? getCarDisplayName({ car, showYear: false }) : '';
  return name.length > 0 ? name : getCargoExpectationFallbackName(row);
}

const normalizeCargoSearchText = (value: string): string => value.replace(/\s+/g, '').toUpperCase();

/**
 * Rows whose plate, car, customer or storage identifier contain `query`, ignoring case and spaces,
 * so a worker can find the wheel set in front of them in a long pickup or delivery list.
 */
export function filterCargoExpectationRows(
  rows: RouteItemCargoExpectationRecord[],
  query: string
): RouteItemCargoExpectationRecord[] {
  const needle = normalizeCargoSearchText(query);
  if (needle.length === 0) {
    return rows;
  }
  return rows.filter((row) => {
    const car = getCargoExpectationCar(row);
    const haystack = [
      car ? getCarLicensePlateText(car) : '',
      getCargoExpectationCarLine(row),
      row.bookingItem?.userGroup.name ?? '',
      row.storageObjectGroup.owner.name,
      row.storageObjectGroup.identifier
    ];
    return haystack.some((text) => normalizeCargoSearchText(text).includes(needle));
  });
}

/** Maps NSDK {@link TireSeason} (`CarWheelSetRecordStorageList.tireSeason.value`) to legacy numeric UI values. */
function carWheelSeasonValueFromNsdk(value: TireSeason | null | undefined): CarWheelSeasonValue | null {
  if (value == null) {
    return null;
  }
  switch (value) {
    case 'SUMMER':
      return CAR_WHEEL_SUMMER_VALUE;
    case 'WINTER':
      return CAR_WHEEL_WINTER_VALUE;
    case 'ALL_SEASON':
      return CAR_WHEEL_ALL_SEASON_VALUE;
    default: {
      const _never: never = value;
      return _never;
    }
  }
}

/**
 * Wheel season for reload row UI: `storageObjectGroup.carWheelSet.tireSeason`, else `tireSeasonOverride` (NSDK).
 * Logs only when a `carWheelSet` exists but season cannot be resolved.
 */
export function getCargoExpectationWheelSeason(row: RouteItemCargoExpectationRecord): CarWheelSeasonValue | null {
  const fromWheelSet = carWheelSeasonValueFromNsdk(row.storageObjectGroup.carWheelSet?.tireSeason?.value);
  if (fromWheelSet != null) {
    return fromWheelSet;
  }
  const fromOverride = carWheelSeasonValueFromNsdk(row.tireSeasonOverride ?? undefined);
  if (fromOverride != null) {
    return fromOverride;
  }
  if (row.storageObjectGroup.carWheelSet != null) {
    console.error(
      'cargo expectation carWheelSet.tireSeason.value missing or invalid',
      row.id,
      row.storageObjectGroup.carWheelSet.tireSeason
    );
  }
  return null;
}

function normalizeUnloadBatchIdentifier(identifier: string): string {
  return identifier.trim();
}

/** Deduped append for the unload batch (case-insensitive). Empty `identifier` after trim is a no-op. */
export function addIdentifierToUnloadBatchList(identifiers: string[], identifier: string): string[] {
  const trimmed = normalizeUnloadBatchIdentifier(identifier);
  if (trimmed === '') {
    return identifiers;
  }
  const needle = trimmed.toUpperCase();
  if (identifiers.some((x) => x.trim().toUpperCase() === needle)) {
    return identifiers;
  }
  return [...identifiers, trimmed];
}

/**
 * Effective reload tab step: respects manual overrides if the step has items, defaults to auto-step guidance otherwise.
 */
function selectReloadLogicalStep(params: {
  expectations: RouteItemCargoExpectationRecord[];
  stepOverride: 'auto' | ReloadStep;
}): ReloadStep {
  const grouped = groupCargoExpectationsByReloadOperation(params.expectations);
  const presentSteps = getPresentReloadSteps(params.expectations);
  const autoStep = getReloadActiveStepAuto(params.expectations);

  // Validate manual override: only use it if that step actually has items
  if (params.stepOverride !== 'auto' && presentSteps.includes(params.stepOverride)) {
    return params.stepOverride;
  }
  // Fall back to auto behavior if override is invalid or 'auto'
  if (autoStep !== 'done') {
    return autoStep;
  }
  if (presentSteps.length > 1) {
    return presentSteps[presentSteps.length - 1]!;
  }
  return getSinglePhaseLogicalStep(grouped) ?? 1;
}

export function selectReloadViewModel(params: {
  expectations: RouteItemCargoExpectationRecord[];
  stepOverride: 'auto' | ReloadStep;
}): {
  grouped: ReloadExpectationsGrouped;
  presentSteps: ReloadStep[];
  autoStep: ReloadStep | 'done';
  logicalStep: ReloadStep;
  stepRows: RouteItemCargoExpectationRecord[];
  /** Where to go once the step on screen is finished; null while it has work left or nothing else does. */
  nextStep: ReloadStep | null;
} {
  const grouped = groupCargoExpectationsByReloadOperation(params.expectations);
  const presentSteps = getPresentReloadSteps(params.expectations);
  const autoStep = getReloadActiveStepAuto(params.expectations);
  const logicalStep = selectReloadLogicalStep(params);
  const stepRows = getActiveRowsForReloadStep(grouped, logicalStep);
  const stepFinished = stepRows.length > 0 && getReloadProgress(stepRows).pending === 0;
  const nextStep = stepFinished && autoStep !== 'done' && autoStep !== logicalStep ? autoStep : null;
  return { grouped, presentSteps, autoStep, logicalStep, stepRows, nextStep };
}

type ScanResultForBarcode =
  | { kind: 'wrong_step' }
  | { kind: 'already_scanned'; row?: RouteItemCargoExpectationRecord }
  | { kind: 'not_on_list' }
  | { kind: 'queue_unload_batch' }
  | { kind: 'call_server'; row?: RouteItemCargoExpectationRecord };

/** Client-side scan routing before `postScanRouteItemCargoExpectationsBulk` (session dedupe, unload batch queue, step). */
export function getScanResultForBarcode(params: {
  identifier: string;
  expectations: RouteItemCargoExpectationRecord[];
  allowedReloadOperations: readonly CargoOperation[];
  isUnloadScanStep: boolean;
  sessionScannedIdentifiers: Set<string>;
  unloadBatchIdentifiers: string[];
}): ScanResultForBarcode {
  const needle = params.identifier.trim().toUpperCase();
  const existingRow = findActiveCargoExpectationByWheelIdentifier(params.expectations, params.identifier);

  if (existingRow != null && !params.allowedReloadOperations.includes(existingRow.cargoOperation)) {
    return { kind: 'wrong_step' };
  }

  if (params.sessionScannedIdentifiers.has(needle)) {
    return { kind: 'already_scanned', row: existingRow };
  }

  if (existingRow?.status === 'scanned' && existingRow.scannedAt) {
    return { kind: 'already_scanned', row: existingRow };
  }

  if (params.isUnloadScanStep) {
    if (existingRow == null) {
      return { kind: 'not_on_list' };
    }
    if (existingRow.status !== 'pending') {
      return { kind: 'already_scanned', row: existingRow };
    }
    if (params.unloadBatchIdentifiers.some((x) => x.trim().toUpperCase() === needle)) {
      return { kind: 'already_scanned', row: existingRow };
    }
    return { kind: 'queue_unload_batch' };
  }

  return { kind: 'call_server', row: existingRow };
}

type ScanBulkInterpretResult =
  | { kind: 'not_on_list' }
  | { kind: 'wrong_step' }
  | { kind: 'success'; expectation: RouteItemCargoExpectationRecord };

/** Map one bulk-scan summary item to UI outcome after the server responds (load step / bulk commit). */
export function interpretScanBulkSummaryForLoadStep(params: {
  summary: RouteItemCargoExpectationScanBulkSummaryItem;
  allowedReloadOperations: readonly CargoOperation[];
}): ScanBulkInterpretResult {
  const { summary, allowedReloadOperations } = params;
  if (summary.anomaly || summary.expectation == null) {
    return { kind: 'not_on_list' };
  }
  if (!allowedReloadOperations.includes(summary.expectation.cargoOperation)) {
    return { kind: 'wrong_step' };
  }
  return { kind: 'success', expectation: summary.expectation };
}
